"use client";

import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import type { SelectedAttributes } from "@jti/commerce/features";
import { parseAttributeParams, serializeAttributeParams } from "@jti/commerce/features";
import { buildUrlWithParams } from "@jti/fe-common/utils";

export interface FilterState {
  selectedCategoryUids: string[];
  selectedAttributes: SelectedAttributes;
}

interface FilterContextType {
  filterState: FilterState;
  setSelectedCategories: (uids: string[]) => void;
  toggleCategory: (uid: string) => void;
  clearFilters: () => void;
  discontinuedProductName?: string | null;
  setDiscontinuedProductName: (name: string | null) => void;
  setInitialCategories: (categories: string[]) => void;
  setSelectedAttributes: (attributes: SelectedAttributes) => void;
  clearAttributes: () => void;
}

const FilterContext = createContext<FilterContextType | undefined>(undefined);

interface FilterProviderProps {
  children: ReactNode;
  initialSelectedCategories?: string[];
}

export function FilterProvider({ children, initialSelectedCategories = [] }: FilterProviderProps) {
  const [filterState, setFilterState] = useState<FilterState>({
    selectedCategoryUids: initialSelectedCategories,
    selectedAttributes: {},
  });
  const [discontinuedProductName, setDiscontinuedProductName] =
    useState<FilterContextType["discontinuedProductName"]>(null);

  // Hydrate applied attribute selections from the URL for deep linking.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const attributesFromUrl = parseAttributeParams(window.location.search);
    if (Object.keys(attributesFromUrl).length === 0) return;

    setFilterState((prev) => ({ ...prev, selectedAttributes: attributesFromUrl }));
  }, []);

  const writeAttributesToUrl = useCallback((attributes: SelectedAttributes) => {
    if (typeof window === "undefined") return;

    const params = serializeAttributeParams(attributes, window.location.search);
    const { pathname, hash } = window.location;

    window.history.replaceState(window.history.state, "", buildUrlWithParams({ pathname, params, hash }));
  }, []);

  const setSelectedCategories = useCallback((uids: string[]) => {
    setFilterState((prev) => ({ ...prev, selectedCategoryUids: uids }));
  }, []);

  const toggleCategory = useCallback((uid: string) => {
    setFilterState((prev) => ({
      ...prev,
      selectedCategoryUids: prev.selectedCategoryUids.includes(uid)
        ? prev.selectedCategoryUids.filter((id) => id !== uid)
        : [...prev.selectedCategoryUids, uid],
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilterState((prev) => ({ ...prev, selectedCategoryUids: [] }));
  }, []);

  const setInitialCategories = useCallback((categories: string[]) => {
    setFilterState((prev) => ({ ...prev, selectedCategoryUids: categories }));
  }, []);

  const setSelectedAttributes = useCallback(
    (attributes: SelectedAttributes) => {
      setFilterState((prev) => ({ ...prev, selectedAttributes: attributes }));
      writeAttributesToUrl(attributes);
    },
    [writeAttributesToUrl]
  );

  const clearAttributes = useCallback(() => {
    setSelectedAttributes({});
  }, [setSelectedAttributes]);

  const contextValue = useMemo(
    () => ({
      filterState,
      setSelectedCategories,
      toggleCategory,
      clearFilters,
      discontinuedProductName,
      setDiscontinuedProductName,
      setInitialCategories,
      setSelectedAttributes,
      clearAttributes,
    }),
    [
      filterState,
      setSelectedCategories,
      toggleCategory,
      clearFilters,
      discontinuedProductName,
      setInitialCategories,
      setSelectedAttributes,
      clearAttributes,
    ]
  );

  return <FilterContext.Provider value={contextValue}>{children}</FilterContext.Provider>;
}

export function useFilter(): FilterContextType;
/** Pass `{ optional: true }` to get `undefined` instead of throwing Error when there is no FilterProvider ancestor. */
export function useFilter(options: { optional: true }): FilterContextType | undefined;
export function useFilter(options?: { optional?: boolean }) {
  const context = useContext(FilterContext);
  if (context === undefined && !options?.optional) {
    throw new Error("useFilter must be used within a FilterProvider");
  }
  return context;
}
